import React, { useState, useEffect, useRef } from 'react';
import { Box, IconButton, CircularProgress, Popper, Typography } from '@mui/material';
import * as Tooltip from '@radix-ui/react-tooltip';
import FavoriteRoundedIcon from '@mui/icons-material/FavoriteRounded';
import FavoriteBorderIcon from '@mui/icons-material/FavoriteBorder';
import DownloadIcon from '../../icons/DownloadIcon';
import FullscreenIcon from '../../icons/FullscreenIcon';
import C2PA from '../common/SVGIcons/C2PA';
import EditIcon from '../../components/artist/Dashboard/Images/edit.svg';
import * as Dialog from '@radix-ui/react-dialog';
import FullScreenImage from '../FullScreenImage';
import FullScreenVideo from '../FullScreenVideo';
import { SaagasImage } from '../../types/SaagasImage';
import downloadImage from '../../utils/downloadImage';
import imagesClient from '../../utils/apiClients/imagesClient';
import useFetchImages from '../../hooks/useFetchImages';
import useC2PA from '../../hooks/useC2PA';
import useSWR, { useSWRConfig } from 'swr';
import api from '../../utils/api';
import { useCredits } from '../../hooks/useCreditBalance';
import artStylesClient from '../../utils/apiClients/artStylesClient';
import { GetArtStylesResponse } from '../../types/ArtStyles';
import { format } from 'date-fns';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { fetchProjectById } from '../../context/InfluencerHelper';
import icon from '../../../public/icon.png';
import { useImageModels } from '../../hooks/useImageModels';
import { ModelCard } from '../../components/models/types';

interface CanvasActionBarProps {
  image: SaagasImage;
  onEdit?: () => void;
  onLike?: () => void;
  onDownload?: () => void;
  viewMode?: 'grid' | 'list';
  c2pa?: boolean;
  mutateProjectImages?: (updater: any, shouldRevalidate?: boolean) => void;
}

const CanvasActionBar: React.FC<CanvasActionBarProps> = ({
  image,
  onEdit,
  onLike,
  onDownload,
  viewMode = 'list',
  mutateProjectImages,
  c2pa = false,
}) => {
  // Dynamic sizes based on view mode
  const barHeight = viewMode === 'grid' ? '50px' : '80px';
  const barPadding = viewMode === 'grid' ? '0px 16px' : '0px 28px';
  const navigate = useNavigate();
  const location = useLocation();
  
  // Check if this is a persona image to use the correct cache instance
  const isPersonaImageForCache = image.imageCategories?.includes('persona') || !!image.projectId;
  const { mutate, images: cachedImages } = useFetchImages(isPersonaImageForCache);
  const { mutate: globalMutate } = useSWRConfig();
  const { sections, isLoading: isLoadingModels } = useImageModels();
  
  // Check if image is uploaded (has "Uploaded" or "User" tags, or modelName is "User Upload", or imageCategories includes "user_upload")
  const isUploadedImage = 
    (image as any)?.tags?.some((tag: string) => tag === 'Uploaded' || tag === 'User') ||
    (image as any)?.modelName === 'User Upload' ||
    (image as any)?.imageCategories?.includes('user_upload') ||
    false;
  const { isLoading, C2PAImageDate } = useC2PA(image?.id, isUploadedImage);
  const [downloading, setDownloading] = useState<boolean>(false);
  const [isDownload, setIsDownload] = useState<boolean>((image as any).downloaded || false);
  const [artStyleIds, setArtStyleIds] = useState<string[]>([]);
  const [, setIsHoveringC2PA] = useState<boolean>(false);
  const [isEditLoading, setIsEditLoading] = useState<boolean>(false);
  const downloadInitiatedByUser = useRef<boolean>(false);
  
  // Read like state directly from cache - this ensures it's always in sync
  // Extract the liked value from cache to use as dependency
  const cachedLikedValue = React.useMemo(() => {
    if (!image.id || !cachedImages || !Array.isArray(cachedImages)) return undefined;
    const cachedImage = cachedImages.find((img: any) => img.id === image.id);
    return cachedImage ? (cachedImage as any).liked : undefined;
  }, [cachedImages, image.id]);
  
  const derivedLiked = React.useMemo(() => {
    if (cachedLikedValue !== undefined) return !!cachedLikedValue;
    return !!image.liked;
  }, [cachedLikedValue, image.liked]);

  // Local state for instant UI updates (avoid waiting for cache re-render)
  const [localLiked, setLocalLiked] = useState<boolean>(derivedLiked);
  const isUpdatingLikeRef = useRef<boolean>(false);

  // Sync local state from cache/props when not actively updating
  useEffect(() => {
    if (isUpdatingLikeRef.current) return;
    setLocalLiked(derivedLiked);
  }, [derivedLiked]);

  const isLiked = localLiked;

  // Update all /media? cache entries so other grids/lists reflect immediately
  const updateAllCacheEntries = React.useCallback((imageId: string, newLikedState: boolean) => {
    try {
      const cache = (globalMutate as any).cache;
      if (!cache) return;

      const cacheKeys: any[] = [];
      cache.forEach((_value: any, key: any) => {
        if (typeof key === 'string' && key.startsWith('/media?')) {
          cacheKeys.push(key);
        }
      });

      cacheKeys.forEach((key: string) => {
        globalMutate(
          key,
          (currentData: any) => {
            if (!currentData) return currentData;
            if (Array.isArray(currentData)) {
              return currentData.map((page: any) => ({
                ...page,
                media: page.media?.map((item: any) =>
                  item.id === imageId ? { ...item, liked: newLikedState } : item
                ),
              }));
            }
            if (currentData.media) {
              return {
                ...currentData,
                media: currentData.media.map((item: any) =>
                  item.id === imageId ? { ...item, liked: newLikedState } : item
                ),
              };
            }
            return currentData;
          },
          { revalidate: false }
        );
      });
    } catch (error) {
      console.warn('Could not update all cache entries:', error);
    }
  }, [globalMutate]);

  // If user likes/unlikes from below grid/list, update the action bar immediately
  useEffect(() => {
    const handleLikeUpdate = (event: Event) => {
      const detail = (event as CustomEvent).detail as { imageId?: string; liked?: boolean } | undefined;
      if (!detail?.imageId) return;
      if (detail.imageId !== image.id) return;
      if (isUpdatingLikeRef.current) return;
      setLocalLiked(!!detail.liked);
    };

    window.addEventListener('imageLikeUpdated', handleLikeUpdate as EventListener);
    return () => window.removeEventListener('imageLikeUpdated', handleLikeUpdate as EventListener);
  }, [image.id]);

  // Update isDownload when image changes
  const imageDownloaded = (image as any).downloaded;
  useEffect(() => {
    setIsDownload(imageDownloaded || false);
    // Reset download flag when image changes to prevent stale state
    downloadInitiatedByUser.current = false;
  }, [image.id, imageDownloaded, image.imageCategories, image.artStyleId]);

  // Fetch art style data for download functionality
  const artStyleId = image.artStyleId;
  const { data: artStyleData } = useSWR<{ data: { credits: number; profileArtStyle: boolean } }>(
    artStyleId ? `/art-styles/v1/art-styles/${artStyleId}` : null, 
    api
  );
  const { data: accountBalanceData, updateCredits } = useCredits();
  
  const credits = artStyleData?.data?.credits || 0;
  const balance = accountBalanceData?.data?.total || 0;
  const hasSufficientCredits = balance >= credits;

  // Check if image is vanilla (free download)
  const isVanilla = image.imageCategories?.[0] === 'vanilla';

  // Check if image is from user's own art style (free download)
  const isOwnArtStyle = C2PAImageDate?.artStyleCreator?.includes(C2PAImageDate?.creator || '') || 
                        (artStyleId && artStyleIds.includes(artStyleId));

  // Fetch user's art styles to check if image is from user's art style
  useEffect(() => {
    async function getArtStyle() {
      try {
        const { data }: { data: GetArtStylesResponse } = await artStylesClient.get('/art-styles?createdBy=me&published=both');
        const ids = data.artStyles.filter(style => style.isPersona !== true).map(style => style.id);
        setArtStyleIds(ids);
      } catch (error) {
        console.error('Error fetching art styles:', error);
      }
    }
    getArtStyle();
  }, []);

  const enableWarning = () => {
    const shouldShowMessage = artStyleId && !artStyleIds.includes(artStyleId) && !isDownload;
    return shouldShowMessage;
  };

  // Check if image is generated with persona (approval workflow). Mypersona treated like art_style.
  const isPersonaImage = (image.imageCategories?.[0] === 'persona' || !!image.projectId) && !image.imageCategories?.includes('mypersona');
  
  // Helper function to check if a URL is a video
  const isVideoUrl = (url: string | null | undefined): boolean => {
    if (!url) return false;
    return url.toLowerCase().endsWith('.mp4') || 
           url.toLowerCase().endsWith('.webm') || 
           url.toLowerCase().endsWith('.mov') ||
           url.toLowerCase().includes('/videos/');
  };
  
  // Check if this is a video
  const isVideo = isVideoUrl(image.url) || 
                  (image as any).type === 'video' || 
                  ((image as any).videoCategories && (image as any).videoCategories.length > 0);

  // Inject global style to ensure tooltip appears above all elements
  useEffect(() => {
    const styleId = 'canvas-action-bar-tooltip-style';
    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');
      style.id = styleId;
      style.textContent = `
        .canvas-action-bar-tooltip,
        [data-radix-tooltip-content].canvas-action-bar-tooltip,
        [data-radix-tooltip-content],
        [data-radix-popper-content-wrapper] {
          z-index: 999999 !important;
        }
        body > [data-radix-tooltip-content],
        body > [data-radix-popper-content-wrapper] {
          z-index: 999999 !important;
        }
      `;
      document.head.appendChild(style);
    }
    return () => {
      const existingStyle = document.getElementById(styleId);
      if (existingStyle) {
        document.head.removeChild(existingStyle);
      }
    };
  }, []);

  // Helper function to find the default edit model: 'Google - Nano Banana Pro'
  // This is the ONLY model that should be used for Edit flow
  const getDefaultEditModel = async (): Promise<ModelCard | null> => {
    const TARGET_MODEL_NAME = 'Google - Nano Banana Pro';
    const TARGET_MODEL_ID = '6929dd38654c3aff1422bde1';
    const TARGET_MODEL_IDENTIFIER = 'fal-ai/gemini-3-pro-image-preview/edit';
    
    // First, try to find it in the Image Editing section
    if (sections && sections.length > 0) {
      const imageEditingSection = sections.find(section => section.title === 'Image Editing');
      
      if (imageEditingSection && imageEditingSection.models && imageEditingSection.models.length > 0) {
        // Look for exact match by name first
        let exactMatch = imageEditingSection.models.find(
          model => model.title === TARGET_MODEL_NAME || model.title === 'Google - Nano Banana Pro Image to Image'
        );
        
        // If not found by name, try by ID
        if (!exactMatch) {
          exactMatch = imageEditingSection.models.find(
            model => model.id === TARGET_MODEL_ID
          );
        }
        
        // If still not found, try by modelIdentifier in apiModel
        if (!exactMatch) {
          exactMatch = imageEditingSection.models.find(
            model => model.apiModel?.modelIdentifier === TARGET_MODEL_IDENTIFIER
          );
        }
        
        if (exactMatch) {
          console.log('Found Google - Nano Banana Pro in sections:', exactMatch);
          return exactMatch;
        }
      }
    }
    
    // If not found in sections (might be excluded), fetch directly from API
    try {
      const response = await api.get<{ models: any[] }>('/image-generation/v1/models?offset=0&size=100');
      
      if (response.data && response.data.models) {
        // Try to find by name first
        let targetModel = response.data.models.find(
          (model: any) => model.name === TARGET_MODEL_NAME && model.modelType === 'image-editing'
        );
        
        // If not found by name, try by ID
        if (!targetModel) {
          targetModel = response.data.models.find(
            (model: any) => model.id === TARGET_MODEL_ID && model.modelType === 'image-editing'
          );
        }
        
        // If still not found, try by modelIdentifier
        if (!targetModel) {
          targetModel = response.data.models.find(
            (model: any) => model.modelIdentifier === TARGET_MODEL_IDENTIFIER && model.modelType === 'image-editing'
          );
        }
        
        if (targetModel) {
          // Convert API model to ModelCard format
          return {
            id: targetModel.id,
            title: targetModel.name,
            description: targetModel.description,
            image: targetModel.previewImageUrl || '',
            selected: false,
            apiModel: targetModel
          };
        }
      }
    } catch (error) {
      console.error('Error fetching model from API:', error);
    }
    
    // If still not found, log error but return null (will be handled in handleEdit)
    console.error(`Could not find "${TARGET_MODEL_NAME}" model. This is required for Edit flow.`);
    return null;
  };

  const handleEdit = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    
    // Always implement the same edit functionality as TopActionBar
    // If onEdit callback is provided, call it as well (for backward compatibility)
    if (onEdit) {
      onEdit();
    }
    
    setIsEditLoading(true);
    
    // ALWAYS use 'Google - Nano Banana Pro' for Edit flow
    // This is the only model that should be preselected when Edit icon is clicked
    let selectedModel: ModelCard | null = null;
    
    // Wait for sections to load if needed
    if (isLoadingModels || !sections || sections.length === 0) {
      console.log('Sections not loaded yet, waiting 200ms...');
      await new Promise(resolve => setTimeout(resolve, 200));
    }
    
    // Fetch the required model (will try sections first, then API)
    selectedModel = await getDefaultEditModel();
    
    // CRITICAL: If model is not found, this is an error condition for Edit flow
    if (!selectedModel) {
      console.error('CRITICAL: Could not find "Google - Nano Banana Pro" model for Edit flow.');
      console.error('Available sections:', sections?.map(s => s.title));
      // Continue anyway - CanvaFormSection might have a fallback, but this should not happen
    } else {
      console.log('Edit flow: Using default model "Google - Nano Banana Pro":', selectedModel);
    }
    
    try {
      // Fetch generation settings to get all the data (width, height, artStyleId, etc.)
      const { data: generationSettings } = await imagesClient.get(`/images/${image.id}/generation-settings`);
      
      // Check location state first for influencer data (from project/detail pages)
      const locationState = (location as any).state;
      
      // Check if we're already on the same route (fairgen-ai-studio)
      const isSameRoute = location.pathname === ROUTES.FAIRGEN_AI_STUDIO;
      
      // Check if this is a persona-based image (for edit: load project; includes mypersona so edit still gets project)
      const isPersonaImage = image.imageCategories?.[0] === 'persona' || 
                            image.imageCategories?.includes('mypersona') ||
                            generationSettings.imageCategories?.[0] === 'persona' ||
                            generationSettings.imageCategories?.includes('mypersona') ||
                            (locationState?.influencer && locationState?.project);
      
      let artStyle = null;
      let artStyleImages = [];
      let influencer = null;
      
      // Get projectId from multiple sources (image object, generation settings)
      const projectId = image.projectId || generationSettings.projectId;
      
      // Handle persona/influencer data
      if (isPersonaImage) {
        // Check location state first for influencer data (from project/detail pages)
        // But only use it if:
        // 1. We're NOT on the same route (to avoid stale data), AND
        // 2. The projectId matches (to ensure it's the correct persona)
        const locationProjectId = locationState?.projectId || locationState?.project?.id || locationState?.influencer?.projectId;
        const shouldUseLocationStateInfluencer = locationState?.influencer && 
                                                 !isSameRoute && 
                                                 projectId && 
                                                 locationProjectId === projectId;
        
        if (shouldUseLocationStateInfluencer) {
          // Use influencer data from location state if available and matches
          influencer = {
            ...locationState.influencer,
            ...(locationState.user && { ...locationState.user }),
            ...(locationState.project && { ...locationState.project }),
            projectId: projectId || locationState.projectId || locationState.project?.id,
            artstyleProfileId: locationState.influencer.artstyleProfileId || locationState.project?.artStyleId
          };
        }
        
        // If we don't have influencer data yet and we have a projectId, fetch it
        if (!influencer && projectId) {
          try {
            const projectData = await fetchProjectById(projectId);
            if (projectData?.influencer) {
              influencer = {
                ...projectData.influencer,
                projectId: projectId,
                artstyleProfileId: projectData.influencer.artstyleProfileId || projectData.artStyleId
              };
            }
          } catch (error) {
            console.error('Failed to fetch project data:', error);
            // If fetch fails and we have location state influencer, use it as fallback
            if (locationState?.influencer && !isSameRoute) {
              influencer = {
                ...locationState.influencer,
                ...(locationState.user && { ...locationState.user }),
                ...(locationState.project && { ...locationState.project }),
                projectId: projectId || locationState.projectId || locationState.project?.id,
                artstyleProfileId: locationState.influencer.artstyleProfileId || locationState.project?.artStyleId
              };
            }
          }
        }
      }
      
      // Handle art style data (only for non-persona images)
      if (generationSettings.artStyleId && !isPersonaImage) {
        // Fetch art style data if it's an art style image
        const { data } = await artStylesClient.get(`/art-styles/${generationSettings.artStyleId}`);
        artStyle = data;
        
        const { data: imagesData } = await artStylesClient.get(`/art-styles/${generationSettings.artStyleId}/images`);
        artStyleImages = imagesData.images;
      }
      
      // Exclude 'image' and 'url' properties from generationSettings
      // For persona images, also exclude 'artStyleId' to prevent it from being treated as an art style
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { image: _, url: __, ...settingsWithoutImage } = generationSettings;
      
      // For persona images, exclude artStyleId to prevent confusion with art style
      let settingsToPass = settingsWithoutImage;
      if (isPersonaImage) {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { artStyleId: ___, ...settingsWithoutArtStyleId } = settingsWithoutImage;
        settingsToPass = settingsWithoutArtStyleId;
      }
      
      navigate(ROUTES.FAIRGEN_AI_STUDIO, {
        state: {
          // Image data (for loading the image)
          image: image.url,
          id: image.id,
          prompt: image.prompt || generationSettings.prompt,
          
          // Generation settings (without artStyleId for persona images)
          ...settingsToPass,
          
          // Image categories and project
          imageCategories: image.imageCategories || generationSettings.imageCategories,
          projectId: projectId || image.projectId || generationSettings.projectId,
          
          // Art style data (if applicable - only for non-persona images)
          ...(artStyle && !isPersonaImage && { style: artStyle, artStyleImages }),
          
          // Persona/influencer data (if applicable) - always pass if we have it
          ...(influencer && { influencer }),
          
          // Set the model for image editing (Google - Nano Banana Pro from editAllowed)
          ...(selectedModel ? { 
            selectedModel: {
              id: selectedModel.id,
              title: selectedModel.title,
              apiModel: selectedModel.apiModel
            }
          } : {}),
          
          // Flag to indicate navigation from Edit icon (for notification and mode sync)
          fromEditIcon: true,
          
          // Timestamp to force state update
          timestamp: Date.now()
        },
        replace: isSameRoute // Use replace when on same route to force state update
      });
      
      // Scroll to paint area after navigation (same as play button)
      if (isSameRoute) {
        setTimeout(() => {
          const canvasArea = document.querySelector('.canvas-main-area') as HTMLElement;
          if (canvasArea) {
            const elementTop = canvasArea.getBoundingClientRect().top;
            const offsetPosition = window.scrollY + elementTop - 100; // 100px offset from top
            window.scrollTo({
              top: Math.max(0, offsetPosition),
              behavior: 'smooth'
            });
          } else {
            // Fallback: scroll to top of page
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }
        }, 100);
      }
      
      // Keep loading state until navigation completes
      // The loading will be cleared when component unmounts or when navigation completes
      setTimeout(() => {
        setIsEditLoading(false);
      }, 500);
    } catch (error) {
      console.error('Failed to fetch generation settings:', error);
      setIsEditLoading(false);
      
      // Fallback: navigate with basic data if API call fails
      navigate(ROUTES.FAIRGEN_AI_STUDIO, {
        state: {
          image: image.url,
          id: image.id,
          prompt: image.prompt,
          imageCategories: image.imageCategories,
          projectId: image.projectId,
          width: image.width ? Number(image.width) : undefined,
          height: image.height ? Number(image.height) : undefined,
          // Set the model for image editing (Google - Nano Banana Pro from editAllowed)
          ...(selectedModel ? { 
            selectedModel: {
              id: selectedModel.id,
              title: selectedModel.title,
              apiModel: selectedModel.apiModel
            }
          } : {}),
          // Flag to indicate navigation from Edit icon (for notification and mode sync)
          fromEditIcon: true,
          timestamp: Date.now()
        },
      });
    }
  };

  const handleLike = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();

    // Get current like state from cache (source of truth)
    const currentLikedState = isLiked;
    const newLikedState = !currentLikedState; // new desired state

    try {
      isUpdatingLikeRef.current = true;
      
      // STEP 1: Update local state IMMEDIATELY for instant UI feedback
      setLocalLiked(newLikedState);
      
      // STEP 2: Dispatch event IMMEDIATELY (synchronously) BEFORE any cache updates
      // This ensures all listeners get the update instantly
      window.dispatchEvent(new CustomEvent('imageLikeUpdated', { 
        detail: { imageId: image.id, liked: newLikedState },
        bubbles: true
      }));

      // STEP 3: Update all caches after event dispatch
      // Update useFetchImages cache (general images) - create new references to trigger re-render
      mutate((images) => {
        if (!images) return images;
        // Create new array and objects to ensure React detects the change
        return images.map((page) => {
          const hasChange = page.media.some((item: any) => item.id === image.id);
          if (!hasChange) return page;
          return {
            ...page,
            media: page.media.map((item: any) =>
              item.id === image.id ? { ...item, liked: newLikedState } : item
            ),
          };
        });
      }, false);

      // Update useFetchImagesByProjectIDInfinite cache (project-specific images)
      if (mutateProjectImages) {
        mutateProjectImages((currentPages: any) => {
          if (!currentPages) return currentPages;
          // Create new array and objects to ensure React detects the change
          return currentPages.map((page: any) => {
            const hasChange = page.images?.some((img: any) => img.id === image.id);
            if (!hasChange) return page;
            return {
              ...page,
              images: page.images?.map((img: any) =>
                img.id === image.id ? { ...img, liked: newLikedState } : img
              ) || page.images,
            };
          });
        }, false);
      }

      // Update all matching /media caches
      updateAllCacheEntries(image.id, newLikedState);

      // Make API call after optimistic update - use current state to determine method
      if (newLikedState) {
        // If not liked, make PUT request to like the image
        await imagesClient.put(`images/${image.id}/like`);
      } else {
        // If already liked, make DELETE request to unlike the image
        await imagesClient.delete(`images/${image.id}/like`);
      }

      // Call optional callback after successful like/unlike
      if (onLike) {
        onLike();
      }
    } catch (error) {
      console.error('Error toggling like:', error);
      // Revert optimistic update on error
      const revertState = !newLikedState;
      setLocalLiked(revertState);
      mutate((images) => {
        if (!images) return images;
        return images.map((page) => {
          const hasChange = page.media.some((item: any) => item.id === image.id);
          if (!hasChange) return page;
          return {
            ...page,
            media: page.media.map((item: any) =>
              item.id === image.id ? { ...item, liked: revertState } : item
            ),
          };
        });
      }, false);
      if (mutateProjectImages) {
        mutateProjectImages((currentPages: any) => {
          if (!currentPages) return currentPages;
          return currentPages.map((page: any) => {
            const hasChange = page.images?.some((img: any) => img.id === image.id);
            if (!hasChange) return page;
            return {
              ...page,
              images: page.images?.map((img: any) =>
                img.id === image.id ? { ...img, liked: revertState } : img
              ) || page.images,
            };
          });
        }, false);
      }
      updateAllCacheEntries(image.id, revertState);
      window.dispatchEvent(new CustomEvent('imageLikeUpdated', { detail: { imageId: image.id, liked: revertState } }));
    }
    finally {
      isUpdatingLikeRef.current = false;
    }
  };

  const handleDownload = async (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    e.preventDefault();
    
    // Mark that this download was initiated by user
    downloadInitiatedByUser.current = true;
    
    // Prevent multiple simultaneous downloads
    if (downloading) {
      downloadInitiatedByUser.current = false;
      return;
    }
    
    // Check if download should be blocked
    const isVanilla = image.imageCategories?.[0] === 'vanilla';
    const shouldBlockDownload = !isOwnArtStyle && !isVanilla && !hasSufficientCredits && !isDownload;
    
    if (shouldBlockDownload) {
      downloadInitiatedByUser.current = false;
      return;
    }

    // Check if this is a first download that will deduct credits
    const willDeductCredits = !isDownload && !isVanilla && !isOwnArtStyle && artStyleId;

    if (onDownload) {
      onDownload();
    }

    try {
      setDownloading(true);
      
      // Always download the image
      await downloadImage(image.id);
      
      // Only update state and credits on first download
      if (!isDownload) {
        setIsDownload(true);
        
        // Update cache to mark image as downloaded
        mutate(data => {
          if (!data) return data;
          return data.map(page => ({
            ...page,
            media: page.media.map(img => 
              img.id === image.id ? { ...img, downloaded: true } : img
            )
          }));
        }, false);
        
        // Update project images cache if provided
        if (mutateProjectImages) {
          mutateProjectImages((currentPages: any) => {
            if (!currentPages) return currentPages;
            return currentPages.map((page: any) => ({
              ...page,
              images: page.images?.map((img: any) =>
                img.id === image.id ? { ...img, downloaded: true } : img
              ) || page.images,
            }));
          }, false);
        }
        
        // Only update credits if:
        // 1. Credits were actually deducted (not for vanilla or own art style)
        // 2. Download was explicitly initiated by user (not automatic)
        if (willDeductCredits && downloadInitiatedByUser.current) {
          updateCredits();
        }
      }
      // If isDownload is true, it's a free re-download (no credit deduction)
    } catch (error) {
      console.error('Failed to download image:', error);
    } finally {
      setDownloading(false);
      // Reset the flag after download completes
      downloadInitiatedByUser.current = false;
    }
  };

  return (
    <Box
      className="canvas-action-bar"
      onClick={(e) => e.stopPropagation()}
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexDirection: 'row',
        // backgroundColor:'red',
        background: 'linear-gradient(to bottom, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.35) 40%, rgba(0,0,0,0.10) 75%, rgba(0,0,0,0.0) 100%)',
        height: barHeight,
        width: '100%',
        maxHeight: barHeight,
        maxWidth: '100%',
        minHeight: barHeight,
        minWidth: '100%',
        flexShrink: 0,
        padding: barPadding,
        flexGrow: 0,
        boxSizing: 'border-box',
        overflow: 'hidden',
        alignContent: 'center',
        // Responsive padding
        '@media (max-width: 600px)': {
          padding: viewMode === 'grid' ? '4px 6px' : '6px 10px',
        },
      }}
    >
      {/* Left Controls */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-start',
          alignContent: 'center',
          gap: viewMode === 'grid' ? '10px' : '14px',
          flexShrink: 0,
          zIndex: 101,
          position: 'relative',
          flexWrap: 'nowrap',
          height: '100%',
        }}
      >
        {/* Edit Icon */}

        {!isPersonaImage && !isVideo && (
          <IconButton
            size="small"
            onClick={handleEdit}
            disabled={isEditLoading}
            sx={{
              color: 'white',
              bgcolor: 'transparent',
              padding: 0,
              width: '24px',
              height: '24px',
              minWidth: '24px',
              minHeight: '24px',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
            }}
          >
            {isEditLoading ? (
              <CircularProgress size={16} sx={{ color: 'white' }} />
            ) : (
              <Box
                component="img"
                src={EditIcon}
                alt="Edit"
                sx={{
                  width: viewMode === 'grid' ? '18px' : '20px',
                  height: viewMode === 'grid' ? '18px' : '20px',
                  display: 'block',
                }}
              />
            )}
          </IconButton>)}

        {/* Like Icon */}
        <IconButton
          size="small"
          onClick={handleLike}
          sx={{
            color: 'white',
            bgcolor: 'transparent',
            padding: 0,
            width: '28px',
            height: '28px',
            minWidth: '28px',
            minHeight: '28px',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
          }}
        >
          {isLiked ? (
            <FavoriteRoundedIcon
              sx={{
                color: '#ef4444',
                fontSize: viewMode === 'grid' ? '22px' : '24px',
                width: viewMode === 'grid' ? '22px' : '24px',
                height: viewMode === 'grid' ? '22px' : '24px',
              }}
            />
          ) : (
            <FavoriteBorderIcon
              sx={{
                color: 'white',
                fontSize: viewMode === 'grid' ? '22px' : '24px',
                width: viewMode === 'grid' ? '22px' : '24px',
                height: viewMode === 'grid' ? '22px' : '24px',
              }}
            />
          )}
        </IconButton>


          
        {/* Content Credential Icon */}
        {/* C2PA - Hide for uploaded images */}
        {!isUploadedImage && (
          <Box
            onMouseEnter={() => setIsHoveringC2PA(true)}
            onMouseLeave={() => setIsHoveringC2PA(false)}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              pointerEvents: 'auto',
              zIndex: 101,
              position: 'relative',
              width: '24px',
              height: '24px',
              minWidth: '24px',
              minHeight: '24px',
              ...(c2pa
                ? { top: '-5px' }
                : { top: viewMode === 'grid' ? '4px' : '2px' }
              ),
              '& .MuiIconButton-root': {
                padding: 0,
                width: '24px',
                height: '24px',
                minWidth: '24px',
                minHeight: '24px',
              },
              '& img': {
                width: viewMode === 'grid' ? '18px' : '20px',
                height: viewMode === 'grid' ? '18px' : '20px',
                display: 'block',
              },
            }}
          >
            {isLoading ? (
              <CircularProgress size={viewMode === 'grid' ? '18px' : '20px'} sx={{ color: 'white' }} />
            ) : (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '100%',
                  height: '100%',
                }}
              >
                <ContentCredentialInfoIconForFairGen image={image} />
              </Box>
            )}
          </Box>
        )}

      

        {/* Download Icon - Hide if persona image */}
        {!isPersonaImage && (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <Box sx={{ display: 'inline-flex' }}>
                <IconButton
                  size="small"
                  onClick={handleDownload}
                  disabled={downloading || (!isOwnArtStyle && image.imageCategories?.[0] !== 'vanilla' && !hasSufficientCredits && !isDownload)}
                  sx={{
                    color: 'white',
                    bgcolor: 'transparent',
                    padding: 0,
                    width: '26px',
                    height: '26px',
                    minWidth: '26px',
                    minHeight: '26px',
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
                    '&:disabled': { opacity: 0.5 },
                    cursor: (!isOwnArtStyle && image.imageCategories?.[0] !== 'vanilla' && !hasSufficientCredits && !isDownload) ? 'not-allowed' : 'pointer',
                  }}
                >
                  {downloading ? (
                    <CircularProgress size={viewMode === 'grid' ? '18px' : '26px'} sx={{ color: 'white' }} />
                  ) : (
                    <DownloadIcon sx={{ width: viewMode === 'grid' ? '18px' : '24px', height: viewMode === 'grid' ? '18px' : '30px', display: 'block' }} />
                  )}
                </IconButton>
              </Box>
            </Tooltip.Trigger>
            {enableWarning() && (
              <Tooltip.Portal container={document.body}>
                <Tooltip.Content
                  className="w-[10rem] p-2 font-inter text-[11px] rounded-[4px] text-center bg-[#181818] border border-[#393939] canvas-action-bar-tooltip"
                  sideOffset={5}
                >
                  {hasSufficientCredits
                    ? `Requires ${credits} credit${credits !== 1 ? 's' : ''}.`
                    : `Credits insufficient, Requires ${credits} credit${credits !== 1 ? 's' : ''}.`}
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
            {isDownload && !isVanilla && !isOwnArtStyle && (
              <Tooltip.Portal container={document.body}>
                <Tooltip.Content
                  className="w-[10rem] p-2 font-inter text-[11px] rounded-[4px] text-center bg-[#181818] border border-[#393939] canvas-action-bar-tooltip"
                  sideOffset={5}
                >
                  Already downloaded, requires no credit.
                </Tooltip.Content>
              </Tooltip.Portal>
            )}
          </Tooltip.Root>
        )}
      </Box>

      {/* Right Control - Fullscreen */}
      <Box 
        sx={{ 
          flexShrink: 0, 
          zIndex: 101, 
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          alignContent: 'center',
          height: '100%',
        }}
      >
        <Dialog.Root>
          <Dialog.Trigger asChild>
            <IconButton
              size="small"
              onClick={(e) => e.stopPropagation()}
              sx={{
                color: 'white',
                bgcolor: 'transparent',
                padding: 0,
                width: '35px',
                height: '35px',
                minWidth: '35px',
                minHeight: '35px',
                maxWidth: '35px',
                maxHeight: '35px',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.3)' },
                '& svg': {
                  width: viewMode === 'grid' ? '28px' : '30px',
                  height: viewMode === 'grid' ? '28px' : '30px',
                  display: 'block',
                },
              }}
            >
              <FullscreenIcon />
            </IconButton>
          </Dialog.Trigger>
          <Dialog.Portal>
            {isVideo ? (
              <FullScreenVideo id={image.id} url={image.url} />
            ) : (
              <FullScreenImage id={image.id} />
            )}
          </Dialog.Portal>
        </Dialog.Root>
      </Box>
    </Box>
  );
};

// Helper components for C2PA functionality







interface ContentCredentialInfoIconProps {
  image: SaagasImage,
  cardPositionStyles?: string,
}

const DEFAULT_CARD_POSITION_STYLES = 'md:p-4 h-fit';
const ContentCredentialInfoIconForFairGen = (props: ContentCredentialInfoIconProps) => {
  const [showCard, setShowCard] = useState<boolean>(false);
  const anchorRef = useRef<HTMLDivElement | null>(null);
  
  // Check if image is uploaded (has "Uploaded" or "User" tags, or modelName is "User Upload", or imageCategories includes "user_upload")
  const isUploadedImage = 
    (props.image as any)?.tags?.some((tag: string) => tag === 'Uploaded' || tag === 'User') ||
    (props.image as any)?.modelName === 'User Upload' ||
    (props.image as any)?.imageCategories?.includes('user_upload') ||
    false;
  const { C2PAImageDate } = useC2PA(props.image.id, isUploadedImage);
  const image: SaagasImage = props.image;

  // console.log('C2PA Data in Icon:', c2paData.C2PAImageDate.Issued_By,props.image.url);

  const onIconClick = (event: React.MouseEvent) => {
    event.stopPropagation();
    setShowCard(!showCard);
  };
  
  // Don't render anything for uploaded images
  if (isUploadedImage) {
    return null;
  }

  return (
    <Box ref={anchorRef} onMouseEnter={() => setShowCard(true)} onMouseLeave={() => setShowCard(false)}>
      <IconButton>
        <Box aria-label="Open Content Credential Modal" className=' lg:inline-block' onClick={onIconClick}>
          <C2PA />
        </Box>
      </IconButton>
      <Popper
        open={showCard}
        anchorEl={anchorRef.current}
        placement="bottom-start"
        disablePortal={false}
        modifiers={[{ name: 'preventOverflow', options: { boundary: 'viewport' } }]}
        sx={{ zIndex: 1500 }}
      >
        <Box onMouseEnter={() => setShowCard(true)} onMouseLeave={() => setShowCard(false)} className={props.cardPositionStyles || DEFAULT_CARD_POSITION_STYLES}>
          <ContentCredentialCardForFairGen image={image} c2paData={C2PAImageDate} />
        </Box>
      </Popper>
    </Box>
  );
};

interface ContentCredentialCardProps {
  image: SaagasImage;
  sx?: string;
  c2paData?: any;
}

const DEFAULT_CARD_STYLE = 'w-[310px] bg-black p-4 rounded-lg z-[1500] h-fit';

const ContentCredentialCardForFairGen = (props: ContentCredentialCardProps) => {
  const location = useLocation();
  
  // Existing logic for Art Style
  const shouldHideArtStyle = location.pathname.startsWith('/creator/project/') || location.pathname.startsWith('/influencer/license-request/');


  const isDashboard = location.pathname === '/creator/dashboard' || location.pathname === '/artist/dashboard';
  const isGeneratedImagesTab = location.hash === '#generated-images';
  
  // Combined flag to hide the influencer name
  const shouldHideInfluencerName = isDashboard && isGeneratedImagesTab;
  
  // Hide influencer name if image was generated using an art style
  const hasArtStyle = !!props.c2paData?.artStyleName;
  const shouldHideInfluencerNameForArtStyle = hasArtStyle;

  const toolLabel = 'AI tool used';
  
  const { sx } = props;
  const createdAt = props?.image?.createdAt ? format(new Date(props.image.createdAt), 'MMM d, yyyy') : null;
  const imageUrl = props.image.url;
  const issued_by = props.c2paData?.Issued_By;
  const toolUsed = props.c2paData?.created;
  const app = props.c2paData?.app;
  
  const licenseBegins = props.c2paData?.LicenseBegins;
  const licenseEnds = props.c2paData?.LicenseEnds;
  const influencerName = props.c2paData?.influencerName;

  const formattedLicenseBegins = licenseBegins ? format(new Date(licenseBegins), 'MMM d, yyyy') : null;
  const formattedLicenseEnds = licenseEnds ? format(new Date(licenseEnds), 'MMM d, yyyy') : null;

  const renderRow = (title: string, data: string) => {
    return (
      <Box
        sx={{
          paddingY: '12px', 
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}
      >
        <Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: '#8E8E8E', fontWeight: 400 }}>{title}</Typography>
        <Typography sx={{ fontFamily: 'Inter', fontSize: '14px', color: '#F5F5F5', fontWeight: 700 }}>{data}</Typography>
      </Box>
    );
  };

  const card = (
    <>
      <div className={`hidden md:block ${sx || DEFAULT_CARD_STYLE}`}>
        <Box display="flex" alignItems="center" gap={2}>
          <Typography
            variant="h6"
            fontWeight="bold"
            mb={1}
            sx={{
              fontSize: '20.16px'
            }}
          >
            Content Credential
          </Typography>
        </Box>

        <Box className="flex items-center gap-3 mb-4">
          <img className="w-[48px] h-[48px] object-cover rounded-md" src={imageUrl} alt="content-credential" />
          <Box>
            <Box className="flex items-center gap-2">
              <img src={icon} className="w-[16px] h-[16px]" alt="icon" />
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 600,
                  color: '#FFFFFF'
                }}
              >
                Issued by {issued_by}
              </Typography>
            </Box>
            {createdAt && (
              <Typography
                sx={{
                  fontFamily: 'Inter',
                  fontSize: '14px',
                  fontWeight: 400,
                  color: '#8E8E8E', 
                  mt: 0.5, 
                  ml: 3
                }}
              >
                {createdAt}
              </Typography>
            )}
          </Box>
        </Box>

        <Box className="w-full h-[1px] bg-[#393939]" />

        <Typography
          sx={{
            fontSize: '14px',
            paddingY: '14px',
          }}
        >
          This image was generated with an AI tool.
        </Typography>

        <Box className="w-full h-[1px] bg-[#393939]" />
        <Box className="w-full h-[1px] bg-[#393939]" />

        {renderRow('App or device ', app)}

        <Box className="w-full h-[1px] bg-[#393939]" />

        {renderRow(toolLabel, toolUsed)}

        <Box className="w-full h-[1px] bg-[#393939]" />

        {!shouldHideArtStyle && props.c2paData?.artStyleName && renderRow('Art Style', props.c2paData?.artStyleName)}
        {!shouldHideArtStyle && props.c2paData?.artStyleName && <Box className="w-full h-[1px] bg-[#393939]" />}
        
        {!shouldHideArtStyle && props.c2paData?.artStyleCreator && renderRow('Art Style Rights Holder', props.c2paData?.artStyleCreator)}
        {!shouldHideArtStyle && props.c2paData?.artStyleCreator && <Box className="w-full h-[1px] bg-[#393939]" />}
        
        {!shouldHideArtStyle && !shouldHideInfluencerName && !shouldHideInfluencerNameForArtStyle && props.c2paData?.artStyleCreator && renderRow('Influencer Name', props.c2paData?.artStyleCreator)}
        {!shouldHideArtStyle && !shouldHideInfluencerName && !shouldHideInfluencerNameForArtStyle && props.c2paData?.artStyleCreator && <Box className="w-full h-[1px] bg-[#393939]" />}
        
        {!shouldHideInfluencerName && !shouldHideInfluencerNameForArtStyle && influencerName && renderRow('Influencer Name', influencerName)}
        {!shouldHideInfluencerName && !shouldHideInfluencerNameForArtStyle && influencerName && <Box className="w-full h-[1px] bg-[#393939]" />}

        {props.c2paData?.creator && renderRow('Generated by', props.c2paData?.creator)}
        {props.c2paData?.creator && <Box className="w-full h-[1px] bg-[#393939]" />}
        
        {formattedLicenseBegins && renderRow('License Start Date', formattedLicenseBegins)}
        {formattedLicenseBegins && <Box className="w-full h-[1px] bg-[#393939]" />}

        {formattedLicenseEnds && renderRow('License End Date', formattedLicenseEnds)}
        {formattedLicenseEnds && <Box className="w-full h-[1px] bg-[#393939]" />}
      </div>
    </>
  );

  return card;
};

export default CanvasActionBar;